import { memo, useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Checkbox } from "@mui/material";
import { makeStyles } from "@mui/styles";
import Spinner from "../../components/Spinner";
import { useSnackbar } from "../../utils/index.js";
import { getOwners, getArchivedOwners, archiveOwners, unarchiveOwners } from "../../api/index.js";
import Table from "../../components/Table";

const useStyles = makeStyles((theme) => ({
    container: {
        width: "90%",
        margin: "2rem auto",
    },
    section: {
        marginBottom: "3rem",
    },
    sectionTitle: {
        color: theme.palette.primary.main,
        marginBottom: "1rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    buttonGroup: {
        display: "flex",
        gap: "1rem",
    },
}));

const ArchiveOwners = () => {
    const classes = useStyles();
    const [isLoading, setIsLoading] = useState(false);
    const [unarchivedOwners, setUnarchivedOwners] = useState([]);
    const [archivedOwners, setArchivedOwners] = useState([]);
    const [selectedUnarchived, setSelectedUnarchived] = useState([]);
    const [selectedArchived, setSelectedArchived] = useState([]);

    const { success, error } = useSnackbar();

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { owners } = await getOwners();
            setUnarchivedOwners(owners || []);

            const { success: scs, owners: archived } = await getArchivedOwners();
            if (scs) {
                setArchivedOwners(archived || []);
            }
        } catch (err) {
            error("Σφάλμα κατά τη φόρτωση των δεδομένων");
        }
        setIsLoading(false);
    }, [error]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSelectUnarchived = (ownerId) => {
        setSelectedUnarchived((prev) =>
            prev.includes(ownerId) ? prev.filter((id) => id !== ownerId) : [...prev, ownerId],
        );
    };

    const handleSelectArchived = (ownerId) => {
        setSelectedArchived((prev) =>
            prev.includes(ownerId) ? prev.filter((id) => id !== ownerId) : [...prev, ownerId],
        );
    };

    const handleArchive = async () => {
        if (selectedUnarchived.length === 0) {
            error("Παρακαλώ επιλέξτε τουλάχιστον μία εκτροφή");
            return;
        }

        setIsLoading(true);
        try {
            const { success: scs, message } = await archiveOwners(selectedUnarchived);
            if (scs) {
                success(message);
                setSelectedUnarchived([]);
                await fetchData();
            } else {
                error(message);
            }
        } catch (err) {
            error("Σφάλμα κατά την αρχειοθέτηση");
        }
        setIsLoading(false);
    };

    const handleUnarchive = async () => {
        if (selectedArchived.length === 0) {
            error("Παρακαλώ επιλέξτε τουλάχιστον μία εκτροφή");
            return;
        }

        setIsLoading(true);
        try {
            const { success: scs, message } = await unarchiveOwners(selectedArchived);
            if (scs) {
                success(message);
                setSelectedArchived([]);
                await fetchData();
            } else {
                error(message);
            }
        } catch (err) {
            error("Σφάλμα κατά την επαναφορά");
        }
        setIsLoading(false);
    };

    const unarchivedColumns = [
        {
            Header: () => (
                <Checkbox
                    sx={{ 
                        color: 'secondary.main', 
                        '&.Mui-checked': { color: 'secondary.main' },
                        '&.MuiCheckbox-indeterminate': { color: 'secondary.main' }
                    }}
                    checked={selectedUnarchived.length === unarchivedOwners.length && unarchivedOwners.length > 0 && unarchivedOwners.every((o) => selectedUnarchived.includes(o._id))}
                    indeterminate={selectedUnarchived.length > 0 && selectedUnarchived.length < unarchivedOwners.length}
                    onChange={(e) => {
                        if (e.target.checked) {
                            setSelectedUnarchived(unarchivedOwners.map((owner) => owner._id));
                        } else {
                            setSelectedUnarchived([]);
                        }
                    }}
                />
            ),
            accessor: "checkbox",
            disableSortBy: true,
            disableFilters: true,
            Cell: ({ row }) => {
                const ownerId = row?.original?._id || row?._original?._id;
                return (
                    <Checkbox
                        sx={{
                            color: 'secondary.main',
                            '&.Mui-checked': { color: 'secondary.main' }
                        }}
                        checked={ownerId ? selectedUnarchived.includes(ownerId) : false}
                        onChange={() => { if (ownerId) handleSelectUnarchived(ownerId); }}
                        onClick={(e) => e.stopPropagation()}
                    />
                );
            },
            width: 50,
        },
        {
            Header: <Typography variant="h8">Κωδικός</Typography>,
            accessor: "code",
        },
        {
            Header: <Typography variant="h8">Επώνυμο</Typography>,
            accessor: "lastName",
        },
        {
            Header: <Typography variant="h8">Όνομα</Typography>,
            accessor: "firstName",
        },
        {
            Header: <Typography variant="h8">Πόλη</Typography>,
            accessor: "city",
        },
    ];

    const archivedColumns = [
        {
            Header: () => (
                <Checkbox
                    sx={{ 
                        color: 'secondary.main', 
                        '&.Mui-checked': { color: 'secondary.main' },
                        '&.MuiCheckbox-indeterminate': { color: 'secondary.main' }
                    }}
                    checked={selectedArchived.length === archivedOwners.length && archivedOwners.length > 0 && archivedOwners.every((o) => selectedArchived.includes(o._id))}
                    indeterminate={selectedArchived.length > 0 && selectedArchived.length < archivedOwners.length}
                    onChange={(e) => {
                        if (e.target.checked) {
                            setSelectedArchived(archivedOwners.map((owner) => owner._id));
                        } else {
                            setSelectedArchived([]);
                        }
                    }}
                />
            ),
            accessor: "checkbox",
            disableSortBy: true,
            disableFilters: true,
            Cell: ({ row }) => {
                const ownerId = row?.original?._id || row?._original?._id;
                return (
                    <Checkbox
                        sx={{
                            color: 'secondary.main',
                            '&.Mui-checked': { color: 'secondary.main' }
                        }}
                        checked={ownerId ? selectedArchived.includes(ownerId) : false}
                        onChange={() => { if (ownerId) handleSelectArchived(ownerId); }}
                        onClick={(e) => e.stopPropagation()}
                    />
                );
            },
            width: 50,
        },
        {
            Header: <Typography variant="h8">Κωδικός</Typography>,
            accessor: "code",
        },
        {
            Header: <Typography variant="h8">Επώνυμο</Typography>,
            accessor: "lastName",
        },
        {
            Header: <Typography variant="h8">Όνομα</Typography>,
            accessor: "firstName",
        },
        {
            Header: <Typography variant="h8">Πόλη</Typography>,
            accessor: "city",
        },
    ];

    return (
        <>
            <Spinner open={isLoading} />
            <Box className={classes.container}>
                {/* Unarchived Owners Section */}
                <Box className={classes.section}>
                    <Box className={classes.sectionTitle}>
                        <Typography variant="h5">Ενεργές Εκτροφές</Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleArchive}
                            disabled={selectedUnarchived.length === 0}
                        >
                            Αρχειοθέτηση ({selectedUnarchived.length})
                        </Button>
                    </Box>
                    <Table
                        data={unarchivedOwners}
                        columns={unarchivedColumns}
                        noDataText="Δεν βρέθηκαν ενεργές εκτροφές"
                        showPageSizeOptions={true}
                    />
                </Box>

                {/* Archived Owners Section */}
                <Box className={classes.section}>
                    <Box className={classes.sectionTitle}>
                        <Typography variant="h5">Αρχειοθετημένες Εκτροφές</Typography>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleUnarchive}
                            disabled={selectedArchived.length === 0}
                            sx={{ color: 'white !important' }}
                        >
                            Επαναφορά ({selectedArchived.length})
                        </Button>
                    </Box>
                    <Table
                        data={archivedOwners}
                        columns={archivedColumns}
                        noDataText="Δεν βρέθηκαν αρχειοθετημένες εκτροφές"
                        showPageSizeOptions={true}
                    />
                </Box>
            </Box>
        </>
    );
};

export default memo(ArchiveOwners);
